import { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import type { Market } from '../dataProvider/types';
import { BanEvasionAnchor } from './BanEvasionAnchor';
import { useBanEvasion } from './useBanEvasion';

/**
 * Ban-evasion detection (D-ban-evasion, Phase 5 surface 3). A relationship surface:
 * suspected same-person accounts evading a ban, DERIVED by matching shared identity
 * signals (deviceId / ipAddress / signupAddress, engine-written stand-ins) plus the
 * existing referral-cluster signal, anchored on banned accounts and ranked by link
 * strength. Detection plus a value-neutral dismiss plus the inherited ban; no new
 * value-moving action.
 *
 * INVESTIGATIVE-GLOBAL: it does NOT read the region switcher, and the match runs
 * CROSS-MARKET (a banned UK account evading via a US account is exactly the case).
 * It is global by default; the region facet filters the ANCHOR's market by exact
 * value, not through marketsForScope. The edge rendering follows referrals but the
 * region handling follows the fraud queue (referrals bands by switcher scope, which
 * would wrongly hide one side of a cross-market link here).
 *
 * SENSITIVE: gated by ROLE, not capability (like the fraud queue, suspensions and
 * the audit log), so a viewer's read-everywhere wildcard cannot reach it. Only the
 * fraud reviewer and super_admin see it.
 */

export const BanEvasionView = () => {
  const [regionFilter, setRegionFilter] = useState<Market | null>(null);
  const { view, isLoading } = useBanEvasion(regionFilter);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Trust and safety
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Ban evasion
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Suspected same-person accounts evading a ban. Each banned account is
        shown with the active accounts that share an identity signal beneath it,
        ranked by how many signals match. The matched signals are the basis for
        a decision, not a score. The match spans both markets, so a ban evaded
        across markets stays visible.
      </Typography>

      <FormControl size="small" sx={{ minWidth: 200, mb: 3 }}>
        <InputLabel id="ban-region-label">Region</InputLabel>
        <Select
          labelId="ban-region-label"
          label="Region"
          value={regionFilter ?? 'all'}
          inputProps={{ 'data-testid': 'ban-region' }}
          onChange={(e) =>
            setRegionFilter(
              e.target.value === 'all' ? null : (e.target.value as Market),
            )
          }
        >
          <MenuItem value="all">All markets</MenuItem>
          <MenuItem value="uk">UK</MenuItem>
          <MenuItem value="us">US</MenuItem>
        </Select>
      </FormControl>

      {isLoading || !view ? (
        <Loading />
      ) : view.anchors.length === 0 ? (
        <Typography color="text.secondary">
          No suspected ban evasion in scope.
        </Typography>
      ) : (
        view.anchors.map((anchor) => (
          <BanEvasionAnchor key={anchor.anchorId} anchor={anchor} />
        ))
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /ban-evasion. ROLE-gated to fraud and super_admin (like
 * the fraud queue, suspensions and the audit log): the surface is sensitive, so a
 * viewer's read-everywhere wildcard must not reach it and an editor has no business
 * here (D-ban-evasion).
 */
export const BanEvasion = () => (
  <RequireAccess roles={['fraud', 'super_admin']}>
    <BanEvasionView />
  </RequireAccess>
);
